import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DirectConnectSyncDataInput,
  useSyncDirectConnectDataMutation,
} from '../../api/graphql/generated';
import { logger } from '../../util/logger';
import {
  DirectConnectTask,
  DirectConnectTaskType,
} from '../model/direct-connect.model';
import { DirectConnectGateSource } from './direct-connect-gate-keeper';
import { useDirectConnectHealthGateContext } from './direct-connect-health-gate';
import {
  DirectConnectTaskExecutionResponse,
  DirectConnectTaskGate,
} from './direct-connect-task-gate';

interface DirectConnectSynchronizeGateProps {
  children: ReactNode;
}

const DirectConnectSynchronizeGate = (
  props: DirectConnectSynchronizeGateProps
) => {
  const { children } = props;
  const { i18n } = useTranslation();
  const [syncDirectConnectData] = useSyncDirectConnectDataMutation();
  const { forceHealthCheck } = useDirectConnectHealthGateContext();

  const executeTask = useCallback(
    async (
      task: DirectConnectTask<DirectConnectSyncDataInput>
    ): Promise<DirectConnectTaskExecutionResponse> => {
      const { payload } = task;

      logger.info('synchronizing direct connect data');

      await syncDirectConnectData({
        variables: {
          input: payload,
        },
      });

      // force health check after sync. we might have synced new data that
      // could change health check response
      forceHealthCheck();

      return {
        success: true,
      };
    },
    [syncDirectConnectData, forceHealthCheck]
  );

  return (
    <DirectConnectTaskGate
      source={DirectConnectGateSource.SYNCHRONIZE_GATE}
      taskType={DirectConnectTaskType.SYNCHRONIZE}
      executeTask={executeTask}
      loaderMessage={i18n.t('common:direct_connect_synchronizing')}
      defaultErrorMessage={i18n.t(
        'common:direct_connect_synchronizing_general_error'
      )}
    >
      {children}
    </DirectConnectTaskGate>
  );
};

export { DirectConnectSynchronizeGate };
